import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getNocList } from "../../../../store/actions/councilActions/nocAction";
import Paging from "../../../Common/Paging";

/*
 * Admin: NOC record.
 *
 * NOCs are issued automatically the moment payment is verified, so there is
 * nothing to approve by hand. This is the record - every NOC applied for, with
 * the student, their center, the type, validity, fee and the issued
 * certificate.
 */
const dmy = (value) =>
  value ? String(value).slice(0, 10).split("-").reverse().join("-") : "-";

const STATUS = {
  0: { label: "Payment Pending", css: "text-info" },
  1: { label: "Issued", css: "text-success" },
  2: { label: "Rejected", css: "text-danger" },
};

const NocList = () => {
  const dispatch = useDispatch();

  const [list, setList] = useState(null);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState("issued");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await dispatch(getNocList({ status, search, page }));
    if (res) {
      setList(res.data);
      setCounts(res.counts || {});
    }
    setLoading(false);
  }, [dispatch, status, search, page]);

  useEffect(() => {
    load();
  }, [load]);

  const pagination = list
    ? {
        page: Number(list.current_page),
        pages: Number(list.last_page),
        total: Number(list.total),
        per_page: Number(list.per_page),
      }
    : null;

  const rows = list?.data || [];

  return (
    <div>
      <div className="BigTitle">NOC List</div>

      <div className="row mt-3 mb-2">
        <div className="col-md-4 mb-2">
          <input
            type="text"
            className="form-control"
            placeholder="Student name, roll/enrollment, center code, purpose or NOC no."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="col-md-3 mb-2">
          <select
            className="form-control"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
          >
            <option value="issued">Issued ({counts.issued || 0})</option>
            <option value="pending">Payment Pending ({counts.pending || 0})</option>
            <option value="rejected">Rejected ({counts.rejected || 0})</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table">
          <thead>
            <tr>
              <th>Sr No.</th>
              <th>Student</th>
              <th>Roll / Enroll</th>
              <th>Center</th>
              <th>Purpose</th>
              <th>Validity</th>
              <th>Fee</th>
              <th>Applied By</th>
              <th>Applied On</th>
              <th>Status</th>
              <th>NOC No.</th>
              <th>Certificate</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={12}>Loading...</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={12}>No NOC found.</td>
              </tr>
            ) : (
              rows.map((row, i) => {
                const s = STATUS[Number(row.status)] || STATUS[0];
                return (
                  <tr key={row.id}>
                    <td>{(pagination.page - 1) * pagination.per_page + i + 1}</td>
                    <td>{row.student_name || "--"}</td>
                    <td>{row.roll_number || row.enrollment || "--"}</td>
                    <td>
                      {row.center_name || "--"}
                      {row.center_code ? ` (${row.center_code})` : ""}
                    </td>
                    <td>{row.purpose || "--"}</td>
                    <td>{row.validity_months ? `${row.validity_months} mo` : "--"}</td>
                    <td>{row.fee != null ? `₹${Number(row.fee)}` : "--"}</td>
                    <td>{row.applied_by || "--"}</td>
                    <td>{dmy(row.created_at)}</td>
                    <td className={s.css}>
                      {s.label}
                      {Number(row.status) === 2 && row.reject_reason ? (
                        <div className="text-muted small">{row.reject_reason}</div>
                      ) : null}
                    </td>
                    <td>{row.noc_number || "--"}</td>
                    <td>
                      {row.link ? (
                        <>
                          <a
                            href={row.link}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-sm btn-light-primary mr-1 mb-1"
                          >
                            Download
                          </a>
                          {row.receipt && (
                            <a
                              href={row.receipt}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-sm btn-light-success mb-1"
                            >
                              Fee Slip
                            </a>
                          )}
                        </>
                      ) : (
                        "--"
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Paging pagination={pagination} onPage={setPage} />
    </div>
  );
};

export default NocList;
